/** @format */

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store/store";
import { useNavigate } from "react-router-dom";
import { EmptyISignInPayload, ISignInPayload } from "./types";
import { unwrapResult } from "@reduxjs/toolkit";
import { signIn } from "../../redux/actions/userActions";
import { setAuthenticationState } from "../../redux/reducers/userReducer";
import { Alert, CircularProgress, alpha } from "@mui/material";
import { Formik, Form } from "formik";
import { signInSchemaValidation } from "../../utility/validation/signInSchemaValidation";
import { signInLoad } from "../../redux/selectors/userSelector";
import { colors } from "../../utility/types/constant";
import { isAuthenticated } from "../../auth/authenticate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const BENEFITS = [
	"Create or join savings circles with people you trust",
	"Automated contributions and scheduled payouts",
	"Bank-level security powered by Stripe",
	"Available in 140+ countries worldwide",
];

const SignIn = () => {
	const navigate = useNavigate();
	const { t } = useTranslation("auth");

	const dispatch = useDispatch<AppDispatch>();

	const isLoading = useSelector(signInLoad);

	// Get the current user from Redux store (if already logged in)
	const currentUser = useSelector((state: RootState) => state.user.userData);

	const [initialValues, setInitialValues] =
		useState<ISignInPayload>(EmptyISignInPayload);

	const [errorMessageAlert, setErrorMessageAlert] = useState("");

	const handleCloseErrorAlert = () => {
		setErrorMessageAlert("");
	};

	// Check if user is already authenticated on mount
	useEffect(() => {
		const checkAuthentication = async () => {
			try {
				// Check if user is already signed in with Cognito
				const currentUserIsAuth = await isAuthenticated();

				if (currentUserIsAuth && currentUser?._id) {
					// User is already authenticated - redirect to their profile
					dispatch(setAuthenticationState(true));
					navigate(`/user/${currentUser._id}`);
				}
			} catch (error: any) {
				// Silently fail - user will just see the sign-in form
				console.log("Auth check error:", error);
			}
		};
		checkAuthentication();
	}, [dispatch, navigate, currentUser]);

	const handleSubmit = async (values: ISignInPayload) => {
		try {
			// call back-end signIn api and update redux state
			const result = await dispatch(signIn(values));

			const data = unwrapResult(result);

			setInitialValues(values);

			if (data) {
				// Check if the user is suspended but can self-resolve (logged in with tokens)
				if (
					data.accountStatus === "suspended" &&
					data.suspensionDetails?.canSelfResolve
				) {
					// User is suspended due to missed contributions - redirect to account-banned page
					// They have tokens now so they can pay their dues
					dispatch(setAuthenticationState(true));
					navigate("/account-banned", {
						state: {
							bannedAt: data.suspensionDetails?.bannedAt,
							reason: data.suspensionDetails?.reason,
							banType: data.suspensionDetails?.banType,
							canSelfResolve: true,
							suspensionDetails: data.suspensionDetails?.suspensionDetails,
						},
					});
					return;
				}

				dispatch(setAuthenticationState(true));
				navigate(`/user/${data?.user?._id}`);
			} else {
				setErrorMessageAlert(t("signIn.errors.invalidCredentials"));
			}
		} catch (error: any) {
			// Check if the error indicates a banned account
			if (error?.error === "Account Suspended" || error?.details?.isBanned) {
				// Handle structured error response for banned account
				// Include full suspension details for self-resolvable bans
				navigate("/account-banned", {
					state: {
						bannedAt: error?.details?.bannedAt,
						reason: error?.details?.reason,
						banType: error?.details?.banType,
						canSelfResolve: error?.details?.canSelfResolve,
						suspensionDetails: error?.details?.suspensionDetails,
					},
				});
			} else if (
				typeof error === "string" &&
				(error.toLowerCase().includes("account suspended") ||
					error.toLowerCase().includes("account banned"))
			) {
				// Handle string error message for banned account
				navigate("/account-banned", {
					state: {
						bannedAt: undefined,
						reason: undefined,
					},
				});
			} else if (
				error?.error === "Account Deleted" ||
				error?.details?.isDeleted
			) {
				// Handle structured error response for deleted account
				navigate("/account-deleted", {
					state: {
						deletedAt: error?.details?.deletedAt,
						reason: error?.details?.reason,
					},
				});
			} else if (
				typeof error === "string" &&
				error.toLowerCase().includes("account deleted")
			) {
				// Handle string error message for deleted account
				navigate("/account-deleted", {
					state: {
						deletedAt: undefined,
						reason: undefined,
					},
				});
			} else {
				// Display other errors
				const errorMessage =
					typeof error === "object" && error?.message ? error.message
						: typeof error === "string" ? error
							: t("signIn.errors.invalidCredentials");
				setErrorMessageAlert(errorMessage);
			}
		}
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				bgcolor: colors.primary,
				display: "flex",
				position: "relative",
				overflow: "hidden",
			}}
		>
			<CssBaseline />
			{/* Background decorative blobs */}
			<Box
				sx={{
					position: "absolute",
					top: -200,
					right: -200,
					width: 600,
					height: 600,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.07)} 0%, transparent 70%)`,
					pointerEvents: "none",
				}}
			/>
			<Box
				sx={{
					position: "absolute",
					bottom: -150,
					left: -150,
					width: 500,
					height: 500,
					borderRadius: "50%",
					background: `radial-gradient(circle, ${alpha(colors.mainSecondary, 0.05)} 0%, transparent 70%)`,
					pointerEvents: "none",
				}}
			/>

			{/* ── Main layout ── */}
			<Box
				sx={{
					display: "flex",
					width: "100%",
					maxWidth: 1100,
					mx: "auto",
					alignItems: "center",
					px: { xs: 2, sm: 3, md: 5 },
					py: { xs: 3, md: 0 },
					minHeight: "100vh",
				}}
			>
				{/* ── Left branding panel (desktop only) ── */}
				<Box
					sx={{
						display: { xs: "none", lg: "flex" },
						flexDirection: "column",
						justifyContent: "center",
						flex: "0 0 46%",
						pr: 8,
						py: 6,
					}}
				>
					{/* Logo */}
					<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 5 }}>
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								overflow: "hidden",
								boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
							}}
						>
							<Box
								component="img"
								src="/images/how-it-works-images/primary-logo-1-1.png"
								alt="FundsyFi"
								sx={{ width: 42, height: 42, objectFit: "contain", display: "block" }}
							/>
						</Box>
						<Typography
							sx={{
								color: "#fff",
								fontFamily: "'Montserrat', sans-serif",
								fontWeight: 800,
								fontSize: "1.5rem",
								letterSpacing: 0.3,
							}}
						>
							FundsyFi
						</Typography>
					</Box>
					<Typography
						sx={{
							color: "#fff",
							fontFamily: "'Montserrat', sans-serif",
							fontWeight: 800,
							fontSize: { lg: "2.2rem", xl: "2.6rem" },
							lineHeight: 1.2,
							mb: 2,
						}}
					>
						Welcome{" "}
						<Box component="span" sx={{ color: colors.mainSecondary }}>
							back.
						</Box>
					</Typography>
					<Typography
						sx={{
							color: alpha("#fff", 0.55),
							mb: 4,
							lineHeight: 1.7,
							fontSize: "0.95rem",
							maxWidth: 380,
						}}
					>
						Sign in to manage your circles, track contributions, and stay connected with your savings community.
					</Typography>
					{BENEFITS.map((benefit, i) => (
						<Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
							<CheckCircleIcon
								sx={{ color: colors.mainSecondary, fontSize: "1.1rem", mt: 0.3, flexShrink: 0 }}
							/>
							<Typography sx={{ color: alpha("#fff", 0.75), fontSize: "0.88rem", lineHeight: 1.55 }}>
								{benefit}
							</Typography>
						</Box>
					))}
				</Box>

				{/* ── Right form card ── */}
				<Box
					sx={{
						flex: 1,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						py: { xs: 0, lg: 4 },
					}}
				>
					<Box
						sx={{
							width: "100%",
							maxWidth: 420,
							bgcolor: "#fff",
							borderRadius: { xs: 3, sm: 4 },
							p: { xs: 3, sm: 4 },
							boxShadow: "0 24px 80px rgba(0, 0, 0, 0.45)",
							position: "relative",
							zIndex: 1,
						}}
					>
						{/* Mobile logo */}
						<Box sx={{ display: { xs: "flex", lg: "none" }, alignItems: "center", gap: 1, mb: 2.5 }}>
							<Box
								sx={{
									width: 32,
									height: 32,
									borderRadius: 1.5,
									overflow: "hidden",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<Box
									component="img"
									src="/images/how-it-works-images/primary-logo-1-1.png"
									alt="FundsyFi"
									sx={{ width: 32, height: 32, objectFit: "contain", display: "block" }}
								/>
							</Box>
							<Typography
								sx={{
									color: colors.primary,
									fontFamily: "'Montserrat', sans-serif",
									fontWeight: 800,
									fontSize: "1.1rem",
								}}
							>
								FundsyFi
							</Typography>
						</Box>

						<Typography
							sx={{
								fontFamily: "'Montserrat', sans-serif",
								fontWeight: 800,
								color: colors.primary,
								fontSize: "1.4rem",
								mb: 0.4,
							}}
						>
							{t("signIn.title")}
						</Typography>
						<Typography variant="body2" sx={{ color: "text.secondary", mb: 2.5, lineHeight: 1.5 }}>
							{t("signIn.subtitle")}
						</Typography>

						{errorMessageAlert && (
							<Alert severity="error" onClose={handleCloseErrorAlert} sx={{ mb: 2, py: 0.5 }}>
								{errorMessageAlert}
							</Alert>
						)}

						<Formik
							initialValues={initialValues}
							validationSchema={signInSchemaValidation}
							onSubmit={handleSubmit}
						>
							{({ values, errors, touched, handleChange }) => (
								<Form>
									<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
										<TextField
											size="small"
											error={!!(errors?.username && touched?.username)}
											helperText={errors.username && touched.username && errors.username}
											required
											fullWidth
											id="email"
											label={t("signIn.labels.email")}
											name="username"
											autoComplete="email"
											autoFocus
											value={values.username}
											onChange={handleChange}
										/>
										<TextField
											size="small"
											error={!!(errors?.password && touched?.password)}
											helperText={errors.password && touched.password && errors.password}
											required
											fullWidth
											name="password"
											label={t("signIn.labels.password")}
											type="password"
											id="password"
											autoComplete="current-password"
											value={values.password}
											onChange={handleChange}
										/>

										<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
											<FormControlLabel
												control={
													<Checkbox
														size="small"
														value="remember"
														sx={{
															color: colors.mainSecondary,
															"&.Mui-checked": { color: colors.mainSecondary },
														}}
													/>
												}
												label={
													<Typography variant="caption">
														{t("signIn.checkbox.rememberMe")}
													</Typography>
												}
											/>
											<Typography
												variant="caption"
												component="a"
												href="/user/forgot-password"
												sx={{
													color: colors.mainSecondary,
													fontWeight: 600,
													textDecoration: "none",
													"&:hover": { textDecoration: "underline" },
												}}
											>
												{t("signIn.links.forgotPassword")}
											</Typography>
										</Box>

										<Button
											disabled={isLoading}
											type="submit"
											fullWidth
											variant="contained"
											sx={{
												mt: 0.5,
												bgcolor: colors.mainSecondary,
												color: colors.primary,
												fontWeight: 800,
												py: 1.2,
												borderRadius: 2,
												textTransform: "none",
												fontSize: "0.95rem",
												letterSpacing: 0.3,
												boxShadow: `0 4px 14px ${alpha(colors.mainSecondary, 0.4)}`,
												"&:hover": {
													bgcolor: colors.mainSecondary,
													boxShadow: `0 6px 20px ${alpha(colors.mainSecondary, 0.5)}`,
												},
											}}
										>
											{isLoading ? (
												<CircularProgress size={22} sx={{ color: colors.primary }} />
											) : (
												t("signIn.buttons.signin")
											)}
										</Button>

										<Grid container justifyContent="center">
											<Grid item>
												<Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.82rem" }}>
													{t("signIn.links.signUp")}{" "}
													<Box
														component="a"
														href="/circle/sign-up"
														sx={{
															color: colors.mainSecondary,
															fontWeight: 700,
															textDecoration: "none",
															"&:hover": { textDecoration: "underline" },
														}}
													>
														{t("signIn.links.signUpLink")}
													</Box>
												</Typography>
											</Grid>
										</Grid>
									</Box>
								</Form>
							)}
						</Formik>

						<Typography
							variant="caption"
							sx={{ display: "block", textAlign: "center", mt: 2.5, color: "text.disabled" }}
						>
							© {new Date().getFullYear()} FundsyFi
						</Typography>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

export default SignIn;
